import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Play, Clock, Dumbbell, ChevronRight, ChevronDown,
  Check, RefreshCw, Loader2, CheckCircle2, Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchApi } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

export default function Training() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [programs, setPrograms] = useState([]);
  const [activeProgram, setActiveProgram] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [completedWorkouts, setCompletedWorkouts] = useState({});
  const [completingWorkout, setCompletingWorkout] = useState(null);

  useEffect(() => {
    loadPrograms();
    loadWorkoutLogs();
  }, []);

  const loadPrograms = async () => {
    try {
      const data = await fetchApi('/programs');
      setPrograms(data);
      if (data.length > 0) {
        setActiveProgram(data[0]);
        // Expand today's workout by default
        const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        const todaySession = data[0].sessions?.find(s => s.day === today);
        if (todaySession) {
          setExpandedDay(todaySession.day);
        }
      }
    } catch (error) {
      console.error('Load programs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkoutLogs = async () => {
    try {
      const data = await fetchApi('/workout/logs?limit=30');
      // Create a map of completed workouts by date and day
      const completed = {};
      const logs = Array.isArray(data) ? data : (data.logs || []);
      logs.forEach(log => {
        const logDate = log.date || log.logged_at;
        if (logDate) {
          const dateKey = new Date(logDate).toDateString();
          const key = `${dateKey}-${log.session_day}`;
          completed[key] = true;
        }
      });
      setCompletedWorkouts(completed);
    } catch (error) {
      console.error('Load workout logs error:', error);
    }
  };

  const isWorkoutCompletedToday = (sessionDay) => {
    const todayKey = `${new Date().toDateString()}-${sessionDay}`;
    return completedWorkouts[todayKey] || false;
  };

  const completeWorkout = async (session) => {
    setCompletingWorkout(session.day);
    try {
      await fetchApi('/workout/log', {
        method: 'POST',
        body: JSON.stringify({
          session_id: activeProgram?.id || 'current',
          session_day: session.day,
          focus: session.focus,
          exercises_count: session.exercises?.length || 0,
          duration_minutes: session.duration_minutes,
        }),
      });
      // Update local state
      const todayKey = `${new Date().toDateString()}-${session.day}`;
      setCompletedWorkouts(prev => ({ ...prev, [todayKey]: true }));
    } catch (error) {
      console.error('Complete workout error:', error);
    } finally {
      setCompletingWorkout(null);
    }
  };

  const regenerateProgram = async () => {
    setRegenerating(true);
    try {
      const newProgram = await fetchApi('/programs/generate', { method: 'POST' });
      setPrograms([newProgram, ...programs]);
      setActiveProgram(newProgram);
    } catch (error) {
      console.error('Regenerate error:', error);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{t('dashboard')}</span>
          </button>
          <h1 className="text-lg font-bold">{t('training_program')}</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={regenerateProgram}
            disabled={regenerating}
            className="text-zinc-400 hover:text-white"
            data-testid="regenerate-btn"
          >
            {regenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {activeProgram ? (
          <>
            {/* Program Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-default mb-8"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-black mb-2">{activeProgram.name}</h2>
                  <p className="text-zinc-400">{activeProgram.description}</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-[#CCFF00]/10 text-[#CCFF00] text-xs font-bold">
                  {t('personalized')}
                </span>
              </div>
              
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-zinc-400">{t('duration')}</span>
                  <p className="font-bold">{activeProgram.weeks} {t('weeks')}</p>
                </div>
                <div>
                  <span className="text-zinc-400">{t('current_week')}</span>
                  <p className="font-bold">{t('week')} {activeProgram.current_week}</p>
                </div>
                <div>
                  <span className="text-zinc-400">{t('sessions')}</span>
                  <p className="font-bold">{activeProgram.sessions?.length || 0} {t('per_week')}</p>
                </div>
              </div>
            </motion.div>

            {/* Weekly Schedule */}
            <h3 className="text-lg font-bold mb-4">{t('weekly_schedule')}</h3>
            <div className="space-y-4">
              {activeProgram.sessions?.map((session, index) => {
                const isExpanded = expandedDay === session.day;
                const isToday = session.day === new Date().toLocaleDateString('en-US', { weekday: 'long' });
                const isCompleted = isWorkoutCompletedToday(session.day);
                
                return (
                  <motion.div
                    key={session.day}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`card-default ${isToday ? 'border-[#CCFF00]/30' : ''} ${isCompleted ? 'border-emerald-500/30' : ''}`}
                    data-testid={`session-${session.day.toLowerCase()}`}
                  >
                    <button
                      onClick={() => setExpandedDay(isExpanded ? null : session.day)}
                      className="w-full flex items-center justify-between"
                      data-testid={`expand-${session.day.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isCompleted ? 'bg-emerald-500 text-white' : isToday ? 'bg-[#CCFF00] text-black' : 'bg-zinc-800'
                        }`}>
                          {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Dumbbell className="w-5 h-5" />}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold">{session.day}</h4>
                            {isCompleted && (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold">
                                Completed
                              </span>
                            )}
                            {isToday && !isCompleted && (
                              <span className="px-2 py-0.5 rounded-full bg-[#CCFF00]/20 text-[#CCFF00] text-xs">
                                {t('today')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-400">{session.focus}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm hidden sm:block">
                          <p className="text-zinc-400">{session.duration_minutes} min</p>
                          <p className="text-zinc-500">{session.exercises?.length || 0} exercises</p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-6 mt-6 border-t border-zinc-800">
                            {/* Warmup */}
                            {session.warmup && (
                              <div className="mb-4 p-3 rounded-lg bg-zinc-900/50">
                                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Warmup</p>
                                <p className="text-sm text-zinc-300">{session.warmup}</p>
                              </div>
                            )}

                            {/* Exercises */}
                            <div className="space-y-3">
                              {session.exercises?.map((exercise, exIndex) => (
                                <div key={exIndex} className="exercise-card" data-testid={`exercise-${exIndex}`}>
                                  <div className="exercise-number">{exIndex + 1}</div>
                                  <div className="flex-1">
                                    <h5 className="font-medium mb-1">{exercise.name}</h5>
                                    <div className="flex flex-wrap gap-3 text-sm text-zinc-400">
                                      {exercise.sets && <span>{exercise.sets} {t('sets')}</span>}
                                      {exercise.reps && <span>{exercise.reps} {t('reps')}</span>}
                                      {exercise.duration && <span>{exercise.duration}</span>}
                                      {exercise.rest && <span>{t('rest')}: {exercise.rest}</span>}
                                    </div>
                                    {exercise.coaching_cues?.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {exercise.coaching_cues.map((cue, cueIndex) => (
                                          <p key={cueIndex} className="text-xs text-zinc-500 flex items-start gap-2">
                                            <Check className="w-3 h-3 text-[#14B8A6] mt-0.5 flex-shrink-0" />
                                            {cue}
                                          </p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Cooldown */}
                            {session.cooldown && (
                              <div className="mt-4 p-3 rounded-lg bg-zinc-900/50">
                                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Cooldown</p>
                                <p className="text-sm text-zinc-300">{session.cooldown}</p>
                              </div>
                            )}

                            {/* Complete Workout Button */}
                            {isCompleted ? (
                              <div className="mt-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
                                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                                <div>
                                  <p className="font-bold text-emerald-400">Workout Completed!</p>
                                  <p className="text-sm text-zinc-400">Great job finishing today's session</p>
                                </div>
                              </div>
                            ) : (
                              <Button
                                onClick={() => completeWorkout(session)}
                                disabled={completingWorkout === session.day}
                                className="w-full mt-6 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
                                data-testid={`complete-${session.day.toLowerCase()}-btn`}
                              >
                                {completingWorkout === session.day ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                )}
                                Complete Workout
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <Dumbbell className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('no_program')}</h2>
            <p className="text-zinc-400 mb-6">{t('generate_program')}</p>
            <Button
              onClick={regenerateProgram}
              disabled={regenerating}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
              data-testid="generate-program-btn"
            >
              {regenerating ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              {t('generate_program')}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
